/**
 * Credit System Constants
 *
 * Single billable pricing class:
 *  - MATRIX: price = f(size, încadrare, nivel risc).
 *
 * Two action sub-classes drive how much classification context is required at
 * charge time:
 *  - SIZE_ONLY: charge only requires size (cells are duplicated across
 *    încadrare × risc). Used for S1/S2 actions that fire before full
 *    classification exists.
 *  - FULL_MATRIX: charge requires size + încadrare + nivel risc.
 *
 * Some FULL_MATRIX cells are null — meaning the action is not available for
 * that combination (e.g. Plan de remediere + Apetit la risc are Esențiale-only).
 *
 * Stage 1/2 wizard steps are FREE — no longer billable.
 *
 * Shared between frontend and backend (Deno edge functions).
 */

// ---------------------------------------------------------------------------
// Pricing dimensions
// ---------------------------------------------------------------------------

export type CompanySize = "S" | "M" | "L";
export type Incadrare = "IMPORTANTA" | "ESENTIALA";
export type NivelRisc = "B" | "I" | "E";

export const ENTITY_SIZE_TO_COMPANY_SIZE: Record<string, CompanySize> = {
  MICRO: "S",
  MICĂ: "S",
  MIC: "S",
  MIJLOCIE: "M",
  MARE: "L",
} as const;

export const INCADRARE_FROM_CLASSIFICATION: Record<string, Incadrare> = {
  ESSENTIAL: "ESENTIALA",
  IMPORTANT: "IMPORTANTA",
} as const;

export const NIVEL_RISC_FROM_CYFUN_LEVEL: Record<string, NivelRisc> = {
  "de BAZĂ": "B",
  IMPORTANT: "I",
  ESENȚIAL: "E",
} as const;

// ---------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------

export const CREDIT_ERROR_CODES = {
  INSUFFICIENT_CREDITS: "insufficient_credits",
  CLASSIFICATION_REQUIRED: "classification_required",
  ACTION_NOT_AVAILABLE: "action_not_available",
} as const;

// ---------------------------------------------------------------------------
// Action types — must match the DB enum credit_action_type
// ---------------------------------------------------------------------------

export const CREDIT_ACTION_TYPES = {
  // Size-only (DNSC + ENIRE)
  NOTIFICARE_INITIALA_DNSC: "notificare_initiala_dnsc",
  ANALIZA_EFECT_PERTURBATOR: "analiza_efect_perturbator",
  DOC_ANALIZA_EFECT_PERTURBATOR_PDF: "doc_analiza_efect_perturbator_pdf",
  DOC_NOTIFICARE_DNSC_PDF: "doc_notificare_dnsc_pdf",
  EVIDENTA_DECIZIE_INREGISTRARE_DNSC: "evidenta_decizie_inregistrare_dnsc",
  EVALUARE_RISC_ENIRE: "evaluare_risc_enire",
  DOC_ANALIZA_RISC_ENIRE_PDF: "doc_analiza_risc_enire_pdf",

  // Full matrix (Maturitate + Plan + Apetit risc)
  CHESTIONAR_PRELIMINAR_V2: "chestionar_preliminar_v2",
  CALCUL_MATURITATE_V2: "calcul_maturitate_v2",
  EVAL_MATURITATE_AI_ASISTATA: "eval_maturitate_ai_asistata",
  S3_RAPORT_PDF_V2: "s3_raport_pdf_v2",
  PLAN_REMEDIERE_V2: "plan_remediere_v2",
  DOC_PLAN_REMEDIERE_PDF: "doc_plan_remediere_pdf",
  STABILIRE_APETIT_RISC: "stabilire_apetit_risc",
  DOC_APETIT_RISC_PDF: "doc_apetit_risc_pdf",

  // Non-billable / system types (ledger-only, never deducted)
  SIGNUP_BONUS: "signup_bonus",
  PURCHASE: "purchase",
  REFUND: "refund",
  OTHER: "other",
} as const;

export type CreditActionType =
  (typeof CREDIT_ACTION_TYPES)[keyof typeof CREDIT_ACTION_TYPES];

// ---------------------------------------------------------------------------
// Matrix shape — single source of truth
// ---------------------------------------------------------------------------

export type MatrixActionType =
  | "notificare_initiala_dnsc"
  | "analiza_efect_perturbator"
  | "doc_analiza_efect_perturbator_pdf"
  | "doc_notificare_dnsc_pdf"
  | "evidenta_decizie_inregistrare_dnsc"
  | "evaluare_risc_enire"
  | "doc_analiza_risc_enire_pdf"
  | "chestionar_preliminar_v2"
  | "calcul_maturitate_v2"
  | "eval_maturitate_ai_asistata"
  | "s3_raport_pdf_v2"
  | "plan_remediere_v2"
  | "doc_plan_remediere_pdf"
  | "stabilire_apetit_risc"
  | "doc_apetit_risc_pdf";

export type BillableActionType = MatrixActionType;

type MatrixCell = Record<NivelRisc, number | null>;
type MatrixSlice = Record<Incadrare, MatrixCell>;
export type MatrixCosts = Record<CompanySize, MatrixSlice>;

// Size-only actions: charge requires only size. Cells are duplicated across
// încadrare × nivel risc within each size.
export const SIZE_ONLY_ACTIONS: ReadonlySet<MatrixActionType> = new Set([
  "notificare_initiala_dnsc",
  "analiza_efect_perturbator",
  "doc_analiza_efect_perturbator_pdf",
  "doc_notificare_dnsc_pdf",
  "evidenta_decizie_inregistrare_dnsc",
  "evaluare_risc_enire",
  "doc_analiza_risc_enire_pdf",
]);

const sizeOnly = (s: number, m: number, l: number): MatrixCosts => ({
  S: {
    IMPORTANTA: { B: s, I: s, E: s },
    ESENTIALA: { B: s, I: s, E: s },
  },
  M: {
    IMPORTANTA: { B: m, I: m, E: m },
    ESENTIALA: { B: m, I: m, E: m },
  },
  L: {
    IMPORTANTA: { B: l, I: l, E: l },
    ESENTIALA: { B: l, I: l, E: l },
  },
});

const DOC_PRICES_FULL: MatrixCosts = {
  S: {
    IMPORTANTA: { B: 8, I: 10, E: 12 },
    ESENTIALA: { B: 10, I: 12, E: 15 },
  },
  M: {
    IMPORTANTA: { B: 12, I: 15, E: 18 },
    ESENTIALA: { B: 15, I: 19, E: 22 },
  },
  L: {
    IMPORTANTA: { B: 20, I: 25, E: 30 },
    ESENTIALA: { B: 25, I: 31, E: 38 },
  },
};

// Plan + Apetit PDFs — Esențiale only (Importantă rows are n/a).
const DOC_PRICES_ES_ONLY: MatrixCosts = {
  S: {
    IMPORTANTA: { B: null, I: null, E: null },
    ESENTIALA: { B: 10, I: 12, E: 15 },
  },
  M: {
    IMPORTANTA: { B: null, I: null, E: null },
    ESENTIALA: { B: 15, I: 19, E: 22 },
  },
  L: {
    IMPORTANTA: { B: null, I: null, E: null },
    ESENTIALA: { B: 25, I: 31, E: 38 },
  },
};

const MAJOR_PRICES_FULL: MatrixCosts = {
  S: {
    IMPORTANTA: { B: 50, I: 62, E: 75 },
    ESENTIALA: { B: 62, I: 78, E: 94 },
  },
  M: {
    IMPORTANTA: { B: 75, I: 94, E: 112 },
    ESENTIALA: { B: 94, I: 117, E: 141 },
  },
  L: {
    IMPORTANTA: { B: 125, I: 156, E: 188 },
    ESENTIALA: { B: 156, I: 195, E: 234 },
  },
};

// Apetit risc — Esențiale only.
const APETIT_PRICES: MatrixCosts = {
  S: {
    IMPORTANTA: { B: null, I: null, E: null },
    ESENTIALA: { B: 62, I: 78, E: 94 },
  },
  M: {
    IMPORTANTA: { B: null, I: null, E: null },
    ESENTIALA: { B: 94, I: 117, E: 141 },
  },
  L: {
    IMPORTANTA: { B: null, I: null, E: null },
    ESENTIALA: { B: 156, I: 195, E: 234 },
  },
};

export const CREDIT_COSTS_MATRIX: Record<MatrixActionType, MatrixCosts> = {
  // Size-only (S1 + S2)
  notificare_initiala_dnsc: sizeOnly(5, 8, 12),
  analiza_efect_perturbator: sizeOnly(8, 12, 20),
  doc_analiza_efect_perturbator_pdf: sizeOnly(8, 12, 20),
  doc_notificare_dnsc_pdf: sizeOnly(8, 12, 20),
  evidenta_decizie_inregistrare_dnsc: sizeOnly(3, 4, 8),
  evaluare_risc_enire: sizeOnly(5, 8, 12),
  doc_analiza_risc_enire_pdf: sizeOnly(5, 8, 12),

  // Full matrix (S3)
  chestionar_preliminar_v2: {
    S: {
      IMPORTANTA: { B: 2, I: 2, E: 3 },
      ESENTIALA: { B: 2, I: 3, E: 4 },
    },
    M: {
      IMPORTANTA: { B: 3, I: 4, E: 4 },
      ESENTIALA: { B: 4, I: 5, E: 6 },
    },
    L: {
      IMPORTANTA: { B: 5, I: 6, E: 8 },
      ESENTIALA: { B: 6, I: 8, E: 9 },
    },
  },
  calcul_maturitate_v2: {
    S: {
      IMPORTANTA: { B: 6, I: 8, E: 9 },
      ESENTIALA: { B: 8, I: 9, E: 11 },
    },
    M: {
      IMPORTANTA: { B: 9, I: 11, E: 14 },
      ESENTIALA: { B: 11, I: 14, E: 17 },
    },
    L: {
      IMPORTANTA: { B: 15, I: 19, E: 22 },
      ESENTIALA: { B: 19, I: 23, E: 28 },
    },
  },
  eval_maturitate_ai_asistata: MAJOR_PRICES_FULL,
  s3_raport_pdf_v2: DOC_PRICES_FULL,

  // S4 — Esențiale only
  plan_remediere_v2: {
    S: {
      IMPORTANTA: { B: null, I: null, E: null },
      ESENTIALA: { B: 125, I: 156, E: 188 },
    },
    M: {
      IMPORTANTA: { B: null, I: null, E: null },
      ESENTIALA: { B: 188, I: 234, E: 281 },
    },
    L: {
      IMPORTANTA: { B: null, I: null, E: null },
      ESENTIALA: { B: 312, I: 391, E: 469 },
    },
  },
  doc_plan_remediere_pdf: DOC_PRICES_ES_ONLY,
  stabilire_apetit_risc: APETIT_PRICES,
  doc_apetit_risc_pdf: DOC_PRICES_ES_ONLY,
} as const;

// ---------------------------------------------------------------------------
// Lookup API
// ---------------------------------------------------------------------------

export type CostContext = {
  size?: CompanySize;
  incadrare?: Incadrare;
  nivelRisc?: NivelRisc;
};

export function isMatrixAction(action: string): action is MatrixActionType {
  return action in CREDIT_COSTS_MATRIX;
}

export function isBillableAction(action: string): action is BillableActionType {
  return isMatrixAction(action);
}

export function isSizeOnlyAction(action: string): boolean {
  return SIZE_ONLY_ACTIONS.has(action as MatrixActionType);
}

export function isFullMatrixAction(action: string): boolean {
  return isMatrixAction(action) && !isSizeOnlyAction(action);
}

class ActionNotAvailableError extends Error {
  readonly code = CREDIT_ERROR_CODES.ACTION_NOT_AVAILABLE;
  readonly action: BillableActionType;
  readonly incadrare: Incadrare;

  constructor(action: BillableActionType, incadrare: Incadrare) {
    super(`Action '${action}' is not available for încadrare '${incadrare}'.`);
    this.name = "ActionNotAvailableError";
    this.action = action;
    this.incadrare = incadrare;
  }
}

export { ActionNotAvailableError };

export function getCreditCost(
  action: BillableActionType,
  ctx: CostContext
): number {
  if (!isMatrixAction(action)) {
    throw new Error(`Unknown billable action: ${action}`);
  }

  if (isSizeOnlyAction(action)) {
    if (!ctx.size) {
      throw new Error(`Size-only action '${action}' requires size.`);
    }
    // Size-only: cells are duplicated; pick a deterministic canonical cell.
    const cell = CREDIT_COSTS_MATRIX[action][ctx.size].IMPORTANTA.B;
    if (cell === null) {
      throw new ActionNotAvailableError(action, "IMPORTANTA");
    }
    return cell;
  }

  if (!ctx.size || !ctx.incadrare || !ctx.nivelRisc) {
    throw new Error(
      `Matrix action '${action}' requires size, incadrare, nivelRisc.`
    );
  }

  const cell =
    CREDIT_COSTS_MATRIX[action][ctx.size][ctx.incadrare][ctx.nivelRisc];
  if (cell === null) {
    throw new ActionNotAvailableError(action, ctx.incadrare);
  }
  return cell;
}

/**
 * Returns true when the action is purchasable for the given (size, încadrare,
 * nivel risc). For size-only actions returns true whenever size is provided.
 * For full-matrix actions returns true only when the cell exists (non-null).
 *
 * UI uses this to gate buttons / surface "Indisponibil" states without
 * triggering a thrown error from getCreditCost.
 */
export function isActionAvailable(
  action: BillableActionType,
  ctx: CostContext
): boolean {
  if (!isMatrixAction(action)) return false;

  if (isSizeOnlyAction(action)) {
    return !!ctx.size;
  }

  if (!ctx.size || !ctx.incadrare || !ctx.nivelRisc) return false;

  return (
    CREDIT_COSTS_MATRIX[action][ctx.size][ctx.incadrare][ctx.nivelRisc] !== null
  );
}

// ---------------------------------------------------------------------------
// Equivalence map — old key → new key
//
// Old DB enum values that are no longer in CREDIT_ACTION_TYPES but may exist
// in credit_transactions ledger from before the cutover. isItPurchased
// honors them as paid receipts for the new equivalent action.
// ---------------------------------------------------------------------------

export const ACTION_PURCHASE_EQUIVALENCE: Partial<
  Record<BillableActionType, readonly string[]>
> = {
  doc_notificare_dnsc_pdf: ["s1_raport_pdf"],
  doc_analiza_risc_enire_pdf: ["s2_raport_pdf"],
  doc_analiza_efect_perturbator_pdf: ["ep_raport_pdf"],
  analiza_efect_perturbator: ["ep_clasificare_nis"],
};

// ---------------------------------------------------------------------------
// Action display config (UI metadata)
// ---------------------------------------------------------------------------

type ActionDisplayConfig = {
  name: string;
  description: string;
  icon: string;
};

export const ACTION_DISPLAY_CONFIG: Record<
  BillableActionType,
  ActionDisplayConfig
> = {
  notificare_initiala_dnsc: {
    name: "Notificare inițială DNSC (Workflow online)",
    description: "Workflow online pentru notificarea inițială către DNSC.",
    icon: "📮",
  },
  analiza_efect_perturbator: {
    name: "Analiza efectului perturbator (Art. 9 OUG 155/2024)",
    description: "Analiza efectului perturbator conform Art. 9 OUG 155/2024.",
    icon: "🌪️",
  },
  doc_analiza_efect_perturbator_pdf: {
    name: "Document oficial – Analiza efectului perturbator (PDF)",
    description: "Document PDF oficial pentru analiza efectului perturbator.",
    icon: "📄",
  },
  doc_notificare_dnsc_pdf: {
    name: "Document oficial – Notificare DNSC (PDF semnabil)",
    description: "Document PDF semnabil pentru notificarea DNSC.",
    icon: "📄",
  },
  evidenta_decizie_inregistrare_dnsc: {
    name: "Evidență Decizie de înregistrare DNSC",
    description: "Evidența deciziei de înregistrare emise de DNSC.",
    icon: "🗂️",
  },
  evaluare_risc_enire: {
    name: "Evaluare de risc ENIRE (Analiza sectorială)",
    description: "Analiza sectorială ENIRE pentru evaluarea riscului.",
    icon: "📈",
  },
  doc_analiza_risc_enire_pdf: {
    name: "Document oficial – Analiza de risc ENIRE (PDF)",
    description: "Document PDF oficial pentru analiza de risc ENIRE.",
    icon: "📄",
  },
  chestionar_preliminar_v2: {
    name: "Evaluare preliminară de maturitate (Scor orientativ CyFun)",
    description:
      "Evaluarea preliminară a maturității în securitate cibernetică.",
    icon: "📋",
  },
  calcul_maturitate_v2: {
    name: "Evaluare completă de maturitate CyFun (autoghidată)",
    description: "Evaluarea completă autoghidată conform cadrului CyFun.",
    icon: "📋",
  },
  eval_maturitate_ai_asistata: {
    name: "Evaluare asistată AI a maturității CyFun",
    description: "Evaluarea maturității CyFun cu asistență AI.",
    icon: "🤖",
  },
  s3_raport_pdf_v2: {
    name: "Document oficial – Evaluare de maturitate (Excel + PDF)",
    description: "Document oficial al evaluării de maturitate (Excel + PDF).",
    icon: "📊",
  },
  plan_remediere_v2: {
    name: "Plan de remediere NIS2 (Construire asistată)",
    description: "Construire asistată a planului de remediere NIS2.",
    icon: "🛠️",
  },
  doc_plan_remediere_pdf: {
    name: "Document oficial – Plan de remediere (DOCX / PDF)",
    description: "Document oficial al planului de remediere.",
    icon: "📄",
  },
  stabilire_apetit_risc: {
    name: "Stabilire apetit la risc (Framework NIS2)",
    description: "Stabilirea apetitului la risc conform Framework NIS2.",
    icon: "🎯",
  },
  doc_apetit_risc_pdf: {
    name: "Document oficial – Declarația de apetit la risc (DOCX / PDF)",
    description: "Document oficial al declarației de apetit la risc.",
    icon: "📄",
  },
};
